// --- START OF FILE Services.jsx ---

import React from 'react';
import { motion } from 'framer-motion';
import {
  BarChart3,
  Network,
  ClipboardCheck,
  Globe,
  Server,
  DollarSign
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const ServiceCard = ({ icon: Icon, title, description, features }) => (
    <motion.div
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        whileHover={{ scale: 1.02, y: -5 }}
        className="glass-effect rounded-xl p-8 group hover:neon-glow transition-all duration-300 flex flex-col h-full"
    >
        <div className="flex items-center mb-6">
            {/* Updated icon background to Peacock Blue to Primary Gold */}
            <div className="w-12 h-12 bg-gradient-to-br from-[hsl(var(--aztec-dark))] to-[hsl(var(--primary-gold))] rounded-lg flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300">
                <Icon className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-bold text-white">{title}</h3>
        </div>
        <p className="text-gray-300 mb-6 leading-relaxed flex-grow">{description}</p>
        <ul className="space-y-2 mb-6">
            {features.map((feature) => (
                <li key={feature} className="flex items-center text-sm text-gray-400">
                    <div className="w-1.5 h-1.5 bg-[hsl(var(--primary-gold))] rounded-full mr-3"></div> {/* Use primary-gold for bullet points */}
                    {feature}
                </li>
            ))}
        </ul>
        <Button
            onClick={() => document.querySelector('#contact').scrollIntoView({ behavior: 'smooth' })}
            variant="outline"
            className="w-full border-[hsl(var(--primary-gold))]/50 text-[hsl(var(--primary-gold))] hover:bg-[hsl(var(--primary-gold))]/10 hover:border-[hsl(var(--primary-gold))] transition-all duration-300 mt-auto"
        >
            Learn More
        </Button>
    </motion.div>
);

const Services = () => {
  const coreSolutions = [
    {
      icon: Network,
      title: 'Low Latency Network Design',
      description: 'Specialized network architecture and optimization for high-performance trading and data environments.',
      features: [
        'Spine-Leaf architectures, VXLAN overlays',
        'Instrumentation expertise (Arista, Cisco, Juniper)',
        'Precision time protocol implementation (PTP/NTP)',
        'Latency benchmarking & troubleshooting',
      ],
    },
    {
      icon: Server,
      title: 'Data Center Migrations',
      description: 'End-to-end project management for seamless and secure migration of critical infrastructure.',
      features: [
        'On-premise, colocation, and cloud migrations',
        'Risk assessment and mitigation planning',
        'Minimal downtime execution strategies',
        'Post-migration performance validation',
      ],
    },
    {
      icon: DollarSign,
      title: 'Cost Savings Audits',
      description: 'In-depth analysis of infrastructure and connectivity to identify significant cost reduction opportunities.',
      features: [
        'Carrier and vendor contract analysis',
        'Hardware lifecycle and efficiency review',
        'Power and cooling optimization audits',
        'Actionable cost-saving recommendations',
      ],
    },
    {
      icon: BarChart3,
      title: 'Trading Analytics',
      description: 'Leveraging packet data analysis and visualization to provide real-time and historical market insights.',
      features: [
        'Python-based packet analyzer script',
        'Grafana-integrated dashboards',
        'Technical documentation & GitHub versioning',
      ],
    },
    {
      icon: ClipboardCheck,
      title: 'Infra Auditing & Project Management',
      description: 'Professional auditing and management for trading infrastructure projects, ensuring compliance and efficiency.',
      features: [
        'Infrastructure health and compliance audits',
        'Project/Program management (scope, milestones)',
        'Vendor and cross-team coordination',
        'Stakeholder review & escalation support',
      ],
    },
    {
      icon: Globe,
      title: 'Client & Exchange Connectivity',
      description: 'Setup, testing, and monitoring of secure, high-performance links to exchanges and clients worldwide.',
      features: [
        'Exchange on-boarding (FIX/FAST/SBE)',
        'VPN/MPLS/SDWAN solutions',
        'End-to-end support and troubleshooting',
      ],
    },
  ];

  return (
    <section id="solutions" className="py-20 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[hsl(var(--accent-violet))]/5 to-transparent"></div> {/* Use accent-violet for background tint */}
      
      <div className="container mx-auto px-6 relative z-10">
        <motion.div initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }} className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="gradient-text">Core Fintech Solutions</span> {/* Now uses Oxanium font */}
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            From infrastructure optimization to strategic consulting, VegaVelox delivers expert-led solutions tailored for the most demanding financial environments.
          </p>
        </motion.div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {coreSolutions.map((service, index) => (
            <ServiceCard key={index} {...service} />
          ))}
        </div>

        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4 }} viewport={{ once: true }} className="text-center mt-16">
          <Button
            onClick={() => document.querySelector('#enterprise-audit').scrollIntoView({ behavior: 'smooth' })}
            size="lg"
            className="bg-gradient-to-r from-[hsl(var(--peacock-blue))] to-[hsl(var(--light-gold))] hover:from-[hsl(var(--peacock-blue))]/90 hover:to-[hsl(var(--light-gold))]/90 text-white px-8 py-4 text-lg font-semibold rounded-lg neon-glow transition-all duration-300 pulse-animation"
          >
            Request Architecture Audit
          </Button>
        </motion.div>
      </div>
    </section>

  );
};

export default Services;
// --- END OF FILE Services.jsx ---